import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ADMIN_SUPPORT_MODULE_ID,
  LEARN_VERTICAL_ID,
  VERTICAL_IDS,
} from "../constants/constants";
import { getAuthToken } from "./useAuth";

export const fetchUserOrders = async (uamId) => {
  const response = await fetch(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`,
    {
      method: "POST",
      headers: {
        accept: "application/json",
        "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uam_id: uamId }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch data: ${response.status} ${response.statusText}`
    );
  }

  const result = await response.json();
  return result;
};

export const updateOrderStatus = async ({ orderId, action }) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ONE_OMS_URL}/update-order-cancellation-request/${orderId}/${action}`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

const uploadCsv = async ({ file, selectedScriptId, uploaderUserId }) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/upload-file?uploaded_by=${uploaderUserId}&selected_script_id=${selectedScriptId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
  };

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to upload file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while discarding the order."
    );
  }
};

export const useUploadCsv = () => {
  return useMutation({ mutationFn: uploadCsv });
};

const fetchHistory = async (user_id) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/get-all-files?uploader_user_id=${user_id}&module_id=${ADMIN_SUPPORT_MODULE_ID}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to fetch history. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders."
    );
  }
};

export const useFetchHistory = (user_id) => {
  return useQuery({
    queryKey: ["history", user_id],
    queryFn: () => fetchHistory(user_id),
  });
};

const fetchProcessedFile = async (fileId) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/download-processed-file/${fileId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useFetchProcessedFile = (fileId) => {
  const query = useQuery({
    queryKey: ["processedFile", fileId],
    queryFn: () => fetchProcessedFile(fileId),
    enabled: !!fileId,
  });
  return {
    ...query,
    fetchProcessedFile,
  };
};

const unblockPhoneNumber = async (phoneNumber) => {
  const url = new URL(`${process.env.REACT_APP_RESET_OTP_URL}`);

  const payload = { isd_code: "+91", phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (
    !data.success ||
    data.success.toLowerCase() !== "unblocked user successfully"
  ) {
    throw new Error(data.message || `Error: ${response.statusText}`);
  }

  return data.detail || data.success;
};

export const useUnblockPhoneNumber = () => {
  return useMutation({ mutationFn: unblockPhoneNumber });
};

const generateOtp = async ({
  phoneNumber,
  admissionNumber,
  productId,
  tenantId,
}) => {
  const url = new URL(`${process.env.REACT_APP_GET_OTP_URL}`);

  const payload = {
    isd_code: process.env.REACT_APP_ISD_CODE,
    phone: phoneNumber,
    admission_number: admissionNumber,
    tenant_id: tenantId,
    product_id: productId,
    mode: "SMS",
  };

  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();

    if (errorData.detail && Array.isArray(errorData.detail)) {
      const firstError = errorData.detail[0];
      const errorMessage =
        firstError.msg || "Validation error while generating OTP";
      throw new Error(errorMessage);
    }

    throw new Error(
      errorData.message || `Failed to generate OTP. Status: ${response.status}`
    );
  }
  const data = await response.json();
  if (data) {
    return data;
  } else {
    throw new Error(data.message || "An error occurred while generating OTP.");
  }
};
export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

const transformLearnUsersWithValidation = (learnUsers) => {
  if (!learnUsers || !Array.isArray(learnUsers.accounts)) return [];

  return learnUsers.accounts.flatMap((account) => {
    if (!Array.isArray(account.profiles)) return [];

    const matchingProfiles = account.profiles.filter(
      (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID)
    );

    if (matchingProfiles.length > 1) {
      throw new Error(
        "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution."
      );
    }

    return matchingProfiles.map((profile) => ({
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      uamId: profile.uam_id,
    }));
  });
};
const fetchCustomerInfoForLearnUsers = async (payload) => {
  const { uam_id } = payload;
  const urlForLearnUsers = new URL(
    `${process.env.REACT_APP_LEARN_USER_SEARCH_URL}`
  );
  const headersForLearnUsers = {
    accept: "application/json",
    "product-id": "1000",
    "tenant-id": "1",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(urlForLearnUsers, {
    method: "POST",
    headers: headersForLearnUsers,
    body: JSON.stringify({ uam_id }),
  });

  const learnUsers = await response.json();

  if (response.status !== 200) {
    const apiMessage =
      learnUsers?.detail?.message ||
      "An error occurred while requesting Learn Customer Information.";
    throw new Error(apiMessage);
  }

  const filtered_users_data =
    transformLearnUsersWithValidation(learnUsers) || [];

  if (filtered_users_data.length === 0) {
    throw new Error("No active Learn user found.Please register first.");
  }

  return filtered_users_data;
};

const fetchCustomerInfoForKHUsers = async (payload) => {
  const { uam_id } = payload;
  const urlForVerifyKHUser = new URL(
    `${process.env.REACT_APP_SCA_USER_SEARCH_URL}`
  );
  const headersForVerifyKHUser = {
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(urlForVerifyKHUser, {
    method: "POST",
    headers: headersForVerifyKHUser,
    body: JSON.stringify({ uam_id }),
  });

  const khUsers = await response.json();

  if (response.status !== 200) {
    const apiMessage =
      khUsers?.detail?.message ||
      "An error occurred while requesting KH Customer Information.";
    throw new Error(apiMessage);
  }

  if (!khUsers.users || khUsers.users.length === 0) {
    throw new Error("No KH user found. Please register first.");
  }

  return khUsers.users.map((user) => ({
    uamId: user.uam_id,
    phone: user.phone,
    firstName: user.first_name,
    lastName: user.last_name,
  }));
};

const fetchCustomerInfo = async (payload) => {
  const { selectedBusinessVertical } = payload;
  if (String(selectedBusinessVertical) === String(VERTICAL_IDS.LEARN)) {
    return fetchCustomerInfoForLearnUsers(payload);
  }

  if (String(selectedBusinessVertical) === String(VERTICAL_IDS.SCA)) {
    return fetchCustomerInfoForKHUsers(payload);
  }

  throw new Error("Unsupported business vertical.");
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

export const useCustomerInfoQuery = (payload) => {
  return useQuery({
    queryKey: ["customerInfo", payload],
    queryFn: () => fetchCustomerInfo(payload),
    enabled: Boolean(payload?.uam_id),
  });
};

const fetchCustomerOrderDetails = async (uamId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const payload = { uam_id: uamId };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchCustomerOrderDetails = (uamId) => {
  return useQuery({
    queryKey: ["customer_order_details", uamId],
    queryFn: () => fetchCustomerOrderDetails(uamId),
    enabled: !!uamId,
  });
};
const acceptModificationRequest = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/accept-order-modification-request/${orderId}`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });
  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_proposal_id;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useAcceptModificationRequest = () => {
  return useMutation({ mutationFn: acceptModificationRequest });
};

const rejecttModificationRequest = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/reject-order-modification-request/${orderId}`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });
  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_proposal_id;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useRejectModificationRequest = () => {
  return useMutation({ mutationFn: rejecttModificationRequest });
};
